/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { Grid } from '@mui/material';

/**
 * The internal dependencies
 */
import {
  GetChangeDto,
  ProcessListForChangeDto,
} from '@codefluegel/zeta-change-typescript-client';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReducedWorkPackagesType } from '../../types/workPackages';
import ProcessTable from '../../components/ProcessCard/ProcessTable';
import TodoCard from '../../components/TodoCard/TodoCard';
import { ReducedServicesType } from '../../types/services';
import { ReducedMaterialsType } from '../../types/materials';
import { getDepartmentById } from '../../utils/utils';

export type CalculationHoursPageProps = {
  isTabDataVisible: boolean;
  processes: ProcessListForChangeDto[];
  workPackages: ReducedWorkPackagesType;
  workMaterials: ReducedMaterialsType;
  workServices: ReducedServicesType;
  isLoading: boolean;
  handleChangeProcess: (fieldName: string, value: number, id: number) => void;
  change: GetChangeDto | null;
  editable: boolean;
};

const CalculationHoursPage = observer(
  ({
    isTabDataVisible,
    processes,
    workPackages,
    workMaterials,
    workServices,
    isLoading,
    handleChangeProcess,
    change,
    editable,
  }: CalculationHoursPageProps) => {
    const [activeProcess, setActiveProcess] = useState<number | null>(null);

    const { t } = useTranslation();

    const handleActiveProcess = useCallback(
      (id: number) => {
        setActiveProcess(id);
      },
      [setActiveProcess],
    );

    return (
      <Grid
        container
        direction="column"
        css={css`
          display: ${isTabDataVisible ? 'flex' : 'none'};
          padding: 19px 0px 12px 0px;
        `}
      >
        <TodoCard text={t('general.infoText.PleaseAddHours')} />

        <Grid
          container
          css={css`
            margin-top: 20px;
            gap: 20px;
          `}
        >
          {processes
            .slice()
            .sort((a, b) => {
              const aDepartment = getDepartmentById(
                a.pId,
                change?.commonProject?.navProcessToCommonProjectRelations,
              );
              const aGroupId = aDepartment?.groupNo ?? 0;

              const bDepartment = getDepartmentById(
                b.pId,
                change?.commonProject?.navProcessToCommonProjectRelations,
              );
              const bGroupId = bDepartment?.groupNo ?? 0;
              return aGroupId - bGroupId;
            })
            .map(process => (
              <ProcessTable
                change={change}
                key={process.id}
                process={process}
                workPackages={workPackages[process.id] || []}
                workServices={workServices[process.id] || []}
                workMaterials={workMaterials[process.id] || []}
                handleChangeProcess={handleChangeProcess}
                handleActiveProcess={handleActiveProcess}
                editable={editable}
                isActive={activeProcess ? activeProcess === process.id : false}
                isLoading={isLoading}
              />
            ))}
        </Grid>
      </Grid>
    );
  },
);

export default CalculationHoursPage;
